/** Trims, removes control characters and caps the length of anything typed into a form. */
export function clean(value: string, max = 500): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim().slice(0, max)
}

export const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[a-zA-Z]{2,}$/

export type SubmitResult = { ok: true; via: 'api' | 'email' } | { ok: false; message: string }

/**
 * Sends a form. With an endpoint configured it POSTs JSON; otherwise it opens the visitor's
 * email app with a ready-to-send message, so nothing is silently lost.
 */
export async function submitForm(endpoint: string, payload: Record<string, string>, fallback: { to: string; subject: string; body: string }): Promise<SubmitResult> {
  // Only send to secure http(s) endpoints.
  if (endpoint && !/^https:\/\//i.test(endpoint) && !/^\//.test(endpoint) && !/^http:\/\/(localhost|127\.0\.0\.1)[:/]/i.test(endpoint)) endpoint = ''
  if (endpoint) {
    try {
      const ctrl = new AbortController()
      const timer = window.setTimeout(() => ctrl.abort(), 15000)
      // Sent as text/plain so the browser makes a "simple" request with no CORS preflight.
      // Google Apps Script (and most form backends) accept this; the body is still JSON.
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload), signal: ctrl.signal, redirect: 'follow' })
      window.clearTimeout(timer)
      if (!res.ok) return { ok: false, message: `Something went wrong on our side (${res.status}). Please try again in a minute.` }
      let data: unknown = null
      try { data = await res.json() } catch { /* a non-JSON reply still counts as success when the status is OK */ }
      if (data && typeof data === 'object' && 'ok' in data && (data as { ok: unknown }).ok === false) {
        const msg = (data as { error?: unknown }).error
        return { ok: false, message: typeof msg === 'string' && msg ? msg : 'We couldn\u2019t save that. Please try again.' }
      }
      return { ok: true, via: 'api' }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return { ok: false, message: 'That took too long. Check your connection and try again.' }
      // A working Google Apps Script always sends a readable reply. A network or CORS failure means the
      // request didn't land (offline, or the script is down or misdeployed), so never report success here.
      console.error('[mauj] form submission failed', err)
      return { ok: false, message: 'We couldn\u2019t save that right now. Please try again in a minute.' }
    }
  }
  const href = `mailto:${fallback.to}?subject=${encodeURIComponent(clean(fallback.subject, 120))}&body=${encodeURIComponent(clean(fallback.body, 1500))}`
  window.location.href = href
  return { ok: true, via: 'email' }
}
